/**
 * <search-discovery> fills the empty state of the header search panel
 * (popular products, popular searches, help links) on demand.
 *
 * The content is rendered by sections/search-discovery.liquid and fetched via
 * the Section Rendering API instead of being server-rendered into every page:
 * the header renders the search panel twice (mobile + desktop), and the product
 * cards inside it used to add markup, stylesheets and images ahead of the page's
 * main content.
 *
 * Loading starts as soon as the visitor shows intent (hover, touch or focus on
 * the search toggle) so the content is usually ready by the time the panel
 * opens, and again on open as a fallback. Both header instances share one
 * request.
 */
if (!customElements.get('search-discovery')) {
  const requests = new Map();

  const fetchSection = (sectionId) => {
    if (!requests.has(sectionId)) {
      const root = window.Shopify?.routes?.root || '/';
      const request = fetch(`${root}?section_id=${encodeURIComponent(sectionId)}`)
        .then((response) => {
          if (!response.ok) throw new Error(`search-discovery: ${response.status}`);
          return response.text();
        })
        .catch((error) => {
          // Allow a later attempt (e.g. the next open) to retry.
          requests.delete(sectionId);
          throw error;
        });
      requests.set(sectionId, request);
    }
    return requests.get(sectionId);
  };

  class SearchDiscovery extends HTMLElement {
    connectedCallback() {
      if (this.isLoaded) return;

      this.details = this.closest('details');
      this.summary = this.details?.querySelector('summary');
      this.onIntent = () => this.load();

      this.summary?.addEventListener('pointerenter', this.onIntent, { once: true });
      this.summary?.addEventListener('touchstart', this.onIntent, { once: true, passive: true });
      this.summary?.addEventListener('focus', this.onIntent, { once: true });
      this.details?.addEventListener('toggle', this.onIntent);
    }

    disconnectedCallback() {
      this.removeListeners();
    }

    removeListeners() {
      this.summary?.removeEventListener('pointerenter', this.onIntent);
      this.summary?.removeEventListener('touchstart', this.onIntent);
      this.summary?.removeEventListener('focus', this.onIntent);
      this.details?.removeEventListener('toggle', this.onIntent);
    }

    load() {
      if (this.isLoaded || this.isLoading) return;
      this.isLoading = true;

      fetchSection(this.dataset.sectionId)
        .then((html) => {
          const content = new DOMParser()
            .parseFromString(html, 'text/html')
            .querySelector('[data-search-discovery-content]');
          if (!content) return;

          this.replaceChildren(...content.childNodes);
          this.isLoaded = true;
          this.removeListeners();

          // details-modal trapped focus when the panel opened; re-trap so the
          // freshly inserted cards and chips are part of the Tab cycle.
          if (this.details?.hasAttribute('open') && typeof trapFocus === 'function') {
            const active = document.activeElement;
            const focusTarget = this.details.contains(active)
              ? active
              : this.details.querySelector('input:not([type="hidden"])');
            trapFocus(this.details.querySelector('[tabindex="-1"]'), focusTarget);
          }
        })
        .catch((error) => console.error(error))
        .finally(() => {
          this.isLoading = false;
        });
    }
  }

  customElements.define('search-discovery', SearchDiscovery);
}
